import _extends from '@babel/runtime/helpers/extends';
import React__default, { useCallback } from 'react';
import PT from 'prop-types';
import { ThemeProvider as ThemeProvider$1 } from 'styled-components';
import { u as useInlinePopper } from '../Popper/useInlinePopper-DwpucsBF.js';
import { P as PopperTrigger } from '../Popper/Popper.styles-CBSd6K9g.js';
import { P as PopperContainer } from '../Popper/PopperContainer-DfaaeWG0.js';
import { A as AnchoredAlertContentContainer } from './AnchoredAlertContent-BZaqnbI7.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';

function modifyPopoverZIndex(theme) {
  return {
    ...theme,
    popover: {
      ...theme.popover,
      zIndex: theme.anchoredAlert.zIndex
    }
  };
}
const NodeSafeHTMLElement = typeof HTMLElement !== 'undefined' ? HTMLElement : Object;
const AnchoredAlert = props => {
  const {
    align = 'center',
    children,
    closeButtonLabel = 'Dismiss Alert',
    closeIconPosition = 'default',
    footer,
    isOpen = false,
    message = '',
    onClick,
    onCloseButtonClick,
    position = 'top',
    spacing = 0,
    trigger,
    type = 'light',
    withCloseButton = true,
    portalRef,
    boundaryRef,
    ...rest
  } = props;
  const placement = align === 'center' ? position : `${position}-${align}`;
  const surfaceType = type === 'light' || type === 'dark' || type === 'blue' ? type : 'light';
  const {
    attributes,
    styles,
    setArrowElement,
    setPopperElement,
    setTriggerElement,
    update
  } = useInlinePopper({
    offsetDistance: spacing,
    placement: placement,
    enableFlip: !!boundaryRef,
    preventOverflow: {
      mainAxis: !!boundaryRef
    },
    withArrow: true,
    boundaryRef
  });

  /**
   * Handles mouse clicks on the <AnchoredAlert /> close button.
   */
  const handleCloseClicked = useCallback(event => {
    event.stopPropagation();

    // call the callback function passed via props.
    onCloseButtonClick === null || onCloseButtonClick === void 0 || onCloseButtonClick(event);
  }, [onCloseButtonClick]);

  /**
   * Catches the click event from the <AnchoredAlert /> trigger and only
   * call the 'onClick' callback if it was passed.
   */
  const preventTriggerClickThrough = useCallback(event => {
    event.stopPropagation();
    if (onClick) {
      onClick(event);
    }
  }, [onClick]);
  const handleAnchoredAlertEntering = useCallback((node, isAppearing) => {
    if (!isAppearing && update !== null) {
      update();
    }
  }, [update]);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(PopperTrigger, {
    onClick: preventTriggerClickThrough,
    ref: setTriggerElement
  }, trigger), /*#__PURE__*/React__default.createElement(ThemeProvider$1, {
    theme: modifyPopoverZIndex
  }, /*#__PURE__*/React__default.createElement(PopperContainer, _extends({
    attributes: attributes,
    closeButtonLabel: closeButtonLabel,
    closeIconPosition: closeIconPosition,
    disablePortal: !portalRef,
    footer: footer,
    handleCloseClicked: handleCloseClicked,
    handlePopoverEntering: handleAnchoredAlertEntering,
    isOpen: isOpen,
    message: message,
    setArrowElement: setArrowElement,
    setPopperElement: setPopperElement,
    styles: styles,
    type: surfaceType,
    withCloseButton: withCloseButton,
    withArrow: true,
    portalRef: portalRef
  }, rest), /*#__PURE__*/React__default.createElement(AnchoredAlertContentContainer, {
    closeButtonLabel: closeButtonLabel,
    closeIconPosition: closeIconPosition,
    footer: footer,
    handleCloseClicked: handleCloseClicked,
    message: message,
    type: surfaceType,
    withCloseButton: withCloseButton
  }, children))));
};
AnchoredAlert.propTypes = {
  /**
   * This element will render the <AnchoredAlert> when clicked. The <AnchoredAlert> will be
   * positioned and aligned relative to this element.
   */
  trigger: PT.element.isRequired,
  /**
   * Justifies the <AnchoredAlert> relative to the trigger.
   */
  align: PT.oneOf(['start', 'center', 'end']),
  /**
   * Content of the AnchoredAlert.
   */
  children: PT.node,
  /**
   * The unique id attribute that is supplied to the <Popper> element. Useful
   * for identifying the `<AnchoredAlert>` in front-end telemetry.
   */
  id: PT.string,
  /**
   * Specifies whether the `<AnchoredAlert>` is visible.
   */
  isOpen: PT.bool,
  /**
   * Content of the AnchoredAlert.
   */
  message: PT.oneOfType([PT.string, PT.node]),
  /**
   * Callback that is called when the specified trigger element is clicked.
   */
  onClick: PT.func,
  /**
   * Callback that is called when the close button on the <Popper> element is clicked.
   */
  onCloseButtonClick: PT.func,
  /**
   * Positions the `<AnchoredAlert>` element relative to the trigger.
   */
  position: PT.oneOf(['top', 'right', 'bottom', 'left']),
  /**
   * Fine grain control over the space between the PIP and the anchor.
   */
  spacing: PT.number,
  /**
   * Specifies the stylistic theme for the `<AnchoredAlert>`.
   */
  type: PT.oneOf(['light', 'dark', 'blue']),
  /**
   * Callback that is called when the close button on the `<AnchoredAlert>` element is clicked.
   */
  withCloseButton: PT.bool,
  /**
   * Screen reader label for the `<AnchoredAlert>` close button. Required when
   * rendering the close button for the `<AnchoredAlert>` to be accessible.
   */
  closeButtonLabel: PT.string,
  /**
   * Content rendered after the main content or message
   */
  footer: PT.node,
  /**
   * Alternate location for the close button
   */
  closeIconPosition: PT.oneOf(['default', 'topCorner']),
  /**
   * A React Ref that contains the HTMLElement this popper will be rendered into.
   */
  portalRef: PT.shape({
    current: PT.instanceOf(NodeSafeHTMLElement)
  }),
  /**
  * A React Ref to a element that popper will attempt to stay inside by flipping
  */
  boundaryRef: PT.shape({
    current: PT.instanceOf(NodeSafeHTMLElement)
  }),
  /**
   * Sets whether the body of the underlying Popper component is focusable when navigating with a keyboard.
   */
  focusableBody: PT.bool
};
AnchoredAlert.defaultProps = {
  align: 'center',
  children: undefined,
  closeButtonLabel: 'Dismiss Alert',
  closeIconPosition: 'default',
  footer: undefined,
  isOpen: false,
  id: undefined,
  message: '',
  onClick: undefined,
  onCloseButtonClick: noop,
  position: 'top',
  spacing: 0,
  type: 'light',
  withCloseButton: true,
  portalRef: undefined,
  boundaryRef: undefined,
  focusableBody: false
};

export { AnchoredAlert as A };
//# sourceMappingURL=AnchoredAlert-D0itRNNT.js.map
